import React, { useEffect } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { AdminMarkets } from './AdminMarkets';
import { SearchesPage } from './SearchesPage';
import { CleanupPage } from './CleanupPage';
import { ReferralsPage } from './ReferralsPage';
import { RegionalPricing } from './RegionalPricing';
import { PaymentMethodsPage } from './PaymentMethodsPage';
import { ManualSubscriptionPage } from './ManualSubscriptionPage';
import { CryptoTransactionsPage } from './CryptoTransactionsPage';
import { LayoutGrid, Search, Trash2, Link as LinkIcon, Globe, CreditCard, UserPlus, Bitcoin } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

export const AdminLayout: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        // Try to perform an admin action to verify access
        const { error } = await supabase
          .from('markets')
          .select('id')
          .limit(1);

        // If there's a permission error, redirect
        if (error?.message?.includes('policy')) {
          navigate('/');
        }
      } catch {
        navigate('/');
      }
    };

    if (!user?.email?.endsWith('@confirmbets.com')) {
      navigate('/');
    } else {
      checkAdminAccess();
    }
  }, [user, navigate]);

  // Don't render anything while checking permissions
  if (!user?.email?.endsWith('@confirmbets.com')) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Admin Dashboard</h1>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <LayoutGrid className="w-4 h-4" />
            <span>Markets</span>
          </button>
          <button
            onClick={() => navigate('/admin/searches')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <Search className="w-4 h-4" />
            <span>Searches</span>
          </button>
          <button
            onClick={() => navigate('/admin/referrals')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <LinkIcon className="w-4 h-4" />
            <span>Referrals</span>
          </button>
          <button
            onClick={() => navigate('/admin/pricing')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <Globe className="w-4 h-4" />
            <span>Regional Pricing</span>
          </button>
          <button
            onClick={() => navigate('/admin/payment-methods')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <CreditCard className="w-4 h-4" />
            <span>Payment Methods</span>
          </button>
          <button
            onClick={() => navigate('/admin/manual-subscription')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            <span>Manual Subscription</span>
          </button>
          <button
            onClick={() => navigate('/admin/crypto-transactions')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <Bitcoin className="w-4 h-4" />
            <span>Crypto Transactions</span>
          </button>
          <button
            onClick={() => navigate('/admin/cleanup')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span>Cleanup</span>
          </button>
        </div>
      </div>

      <Routes>
        <Route path="/" element={<AdminMarkets />} />
        <Route path="/searches" element={<SearchesPage />} />
        <Route path="/referrals" element={<ReferralsPage />} />
        <Route path="/pricing" element={<RegionalPricing />} />
        <Route path="/payment-methods" element={<PaymentMethodsPage />} />
        <Route path="/manual-subscription" element={<ManualSubscriptionPage />} />
        <Route path="/crypto-transactions" element={<CryptoTransactionsPage />} />
        <Route path="/cleanup" element={<CleanupPage />} />
      </Routes>
    </div>
  );
};